import { useEffect, useState, useCallback, use } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ConnectionStatus } from '../../document/constants';
import { useToaster } from '../../core/components/Toast';

interface UsePersonalInboxConnectionProps {
    t: (key: string) => string;
    authForm: any;
    refetchUser?: () => Promise<void>;
}

interface PostClassifyConfig {
    keywords: string[];
    emails: string[];
}

export const usePersonalInboxConnection = ({ t, authForm, refetchUser }: UsePersonalInboxConnectionProps) => {
    const [isConnected, setIsConnected] = useState(false);
    const [connectedChannels, setConnectedChannels] = useState([]);
    const [postClassifyConfig, setPostClassifyConfig] = useState({} as PostClassifyConfig);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [hasErrorIntegration, setHasErrorIntegration] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const { publishToast } = useToaster();

    useEffect(() => {
        const outlookIntegration = searchParams.get('outlookIntegration');
        if (outlookIntegration === 'success') {
            setIsConnected(true);
            setModalStep(1);
            setIsModalOpen(true);
        } else if (outlookIntegration === 'error') {
            setHasErrorIntegration(true);
            setIsModalOpen(false);
        }
    }, [searchParams, setSearchParams]);

    const handleOpenModal = useCallback(() => {
        setModalStep(0);
        setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalStep(0);
        setIsModalOpen(false);
        setSearchParams(new URLSearchParams());
    }, [setSearchParams]);

    const handlePause = useCallback(
        (form: any) => {
            form.setValue('status', ConnectionStatus.Paused);
            publishToast({ description: t('personalSmartInbox.pauseSuccessful'), status: 'success' });
        },
        [publishToast, t]
    );

    const handleResume = useCallback(
        (form: any) => {
            publishToast({
                description: t('personalSmartInbox.connectionResumedSuccessful'),
                status: 'success',
            });
            form.setValue('status', ConnectionStatus.Connected);
        },
        [publishToast, t]
    );

    const handleDisconnect = useCallback(
        (form: any) => {
            setIsDisconnecting(false);
            form.setValue('status', ConnectionStatus.Disconnected);
            publishToast({ description: t('personalSmartInbox.disconnectionSuccessful'), status: 'success' });
        },
        [publishToast, t]
    );

    return {
        isConnected,
        setIsConnected,
        connectedChannels,
        setConnectedChannels,
        postClassifyConfig,
        setPostClassifyConfig,
        isModalOpen,
        setIsModalOpen,
        modalStep,
        setModalStep,
        isDisconnecting,
        setIsDisconnecting,
        hasErrorIntegration,
        setHasErrorIntegration,
        searchParams,
        setSearchParams,
        publishToast,
        handleOpenModal,
        handleCloseModal,
        handlePause,
        handleResume,
        handleDisconnect,
    };
};
